// Echte Gauss-Naeherung fuer Canvas-Unschaerfe OHNE ctx.filter — der
// Safari-Rueckfall (WebKit parst 'blur(…)', wendet es aber nicht an; iOS < 18
// lehnt es ganz ab; Erkennung siehe image-mask-shadow-blur.ts).
//
// Verfahren: dreifacher Box-Blur, separabel mit gleitendem Fenster (Ivan
// Kutskirs Standard-Zerlegung) — drei Kaesten naehern die Gauss-Glocke nach
// dem zentralen Grenzwertsatz so gut an, dass das Ergebnis von der nativen
// blur()-Optik praktisch nicht zu unterscheiden ist. Der fruehere
// Skalier-Trick (mehrfach halbieren + hochskalieren) war Jans Auge zu grob
// ("sieht noch nicht so richtig gut aus", 02.08.2026): er erzeugt blockige
// Verlaeufe statt weicher Glocken.
//
// In PREMULTIPLIZIERTEM Alpha gerechnet — sonst mischen sich an transparenten
// Kanten die (bedeutungslosen) RGB-Werte durchsichtiger Pixel ein und saeumen
// das Ergebnis dunkel. `radius` entspricht der Sigma-Bedeutung von CSS
// blur(<radius>). Kosten: einige Sweeps ueber die Pixel, einmalig pro
// Neu-Backen einer Textur (nur Safari zahlt sie; Chrome nimmt ctx.filter).

// Kastenbreiten (ungerade) fuer n Box-Blur-Durchgaenge, die zusammen einer
// Gauss-Unschaerfe mit Standardabweichung sigma entsprechen.
function boxesForGauss(sigma: number, n: number): number[] {
    const wIdeal = Math.sqrt((12 * sigma * sigma) / n + 1);
    let wl = Math.floor(wIdeal);
    if (wl % 2 === 0) wl -= 1;
    const wu = wl + 2;
    const mIdeal = (12 * sigma * sigma - n * wl * wl - 4 * n * wl - 3 * n) / (-4 * wl - 4);
    const m = Math.round(mIdeal);
    return Array.from({ length: n }, (_, i) => (i < m ? wl : wu));
}

// Ein horizontaler Box-Durchgang (Radius r = halbe Kastenbreite), Kanten
// werden mit dem Randpixel fortgesetzt. src -> dst, Zeile fuer Zeile.
function boxBlurH(src: Float32Array, dst: Float32Array, w: number, h: number, r: number) {
    const iarr = 1 / (r + r + 1);
    for (let i = 0; i < h; i += 1) {
        let ti = i * w;
        let li = ti;
        let ri = ti + r;
        const rowEnd = i * w + w - 1;
        const fv = src[ti];
        const lv = src[rowEnd];
        let val = (r + 1) * fv;
        for (let j = 0; j < r; j += 1) val += src[Math.min(ti + j, rowEnd)];
        for (let j = 0; j <= r; j += 1) {
            val += (ri <= rowEnd ? src[ri] : lv) - fv;
            ri += 1;
            dst[ti] = val * iarr;
            ti += 1;
        }
        for (let j = r + 1; j < w - r; j += 1) {
            val += src[ri] - src[li];
            ri += 1;
            li += 1;
            dst[ti] = val * iarr;
            ti += 1;
        }
        for (let j = Math.max(w - r, r + 1); j < w; j += 1) {
            val += lv - src[li];
            li += 1;
            dst[ti] = val * iarr;
            ti += 1;
        }
    }
}

// Dito vertikal (Schrittweite w statt 1).
function boxBlurV(src: Float32Array, dst: Float32Array, w: number, h: number, r: number) {
    const iarr = 1 / (r + r + 1);
    for (let i = 0; i < w; i += 1) {
        let ti = i;
        let li = ti;
        let ri = ti + r * w;
        const colEnd = i + (h - 1) * w;
        const fv = src[ti];
        const lv = src[colEnd];
        let val = (r + 1) * fv;
        for (let j = 0; j < r; j += 1) val += src[Math.min(ti + j * w, colEnd)];
        for (let j = 0; j <= r; j += 1) {
            val += (ri <= colEnd ? src[ri] : lv) - fv;
            ri += w;
            dst[ti] = val * iarr;
            ti += w;
        }
        for (let j = r + 1; j < h - r; j += 1) {
            val += src[ri] - src[li];
            ri += w;
            li += w;
            dst[ti] = val * iarr;
            ti += w;
        }
        for (let j = Math.max(h - r, r + 1); j < h; j += 1) {
            val += lv - src[li];
            li += w;
            dst[ti] = val * iarr;
            ti += w;
        }
    }
}

/**
 * Unschaerft die Canvas IN PLACE (und gibt sie zurueck). `radius` wie bei
 * CSS blur(<radius>). radius <= 0 ist ein No-op.
 */
export function blurCanvasFallback(src: HTMLCanvasElement, radius: number): HTMLCanvasElement {
    const w = src.width;
    const h = src.height;
    if (radius <= 0 || w < 2 || h < 2) return src;
    const ctx = src.getContext("2d", { willReadFrequently: true });
    if (!ctx) return src;
    const image = ctx.getImageData(0, 0, w, h);
    const d = image.data;
    const n = w * h;

    // Premultiplizieren, kanalweise in Floats (fuer den gleitenden Mittelwert)
    const channels = [new Float32Array(n), new Float32Array(n), new Float32Array(n), new Float32Array(n)];
    for (let i = 0; i < n; i += 1) {
        const a = d[i * 4 + 3] / 255;
        channels[0][i] = d[i * 4] * a;
        channels[1][i] = d[i * 4 + 1] * a;
        channels[2][i] = d[i * 4 + 2] * a;
        channels[3][i] = d[i * 4 + 3];
    }

    const tmp = new Float32Array(n);
    for (const box of boxesForGauss(Math.max(0.5, radius), 3)) {
        // halbe Kastenbreite; an kleine Canvases klammern (das klassische
        // Schema verlangt 2r+1 <= Kantenlaenge)
        const rH = Math.min((box - 1) / 2, Math.floor((w - 1) / 2));
        const rV = Math.min((box - 1) / 2, Math.floor((h - 1) / 2));
        for (const c of channels) {
            boxBlurH(c, tmp, w, h, rH);
            boxBlurV(tmp, c, w, h, rV);
        }
    }

    // Zurueck nach straight alpha (Uint8Clamped rundet/klammert beim Zuweisen)
    for (let i = 0; i < n; i += 1) {
        const a = channels[3][i];
        const inv = a > 0.5 ? 255 / a : 0;
        d[i * 4] = channels[0][i] * inv;
        d[i * 4 + 1] = channels[1][i] * inv;
        d[i * 4 + 2] = channels[2][i] * inv;
        d[i * 4 + 3] = a;
    }
    ctx.putImageData(image, 0, 0);
    return src;
}
